import { fmxBatchAll, fmxErrorMessage } from './apiClient';

// Runs the reverse of a push, from StepExport's undo phase.
//
// Params:
//   push:  { mode, endpoint_base, created_ids, update_snapshots }  (shape from project_pushes row,
//          or the in-memory equivalent built during a just-completed push).
//   creds: { projectId }  — the batch proxy looks up + decrypts the saved creds server-side.
//   onProgress?: ({ done, total, last: { ok, label } }) => void
//
// Returns: { reversed, failed, failures: [{ id, error }] }
export async function executePushUndo(push, creds, onProgress) {
  const { mode, endpoint_base: base } = push;

  const items = mode === 'create'
    ? Object.entries(push.created_ids || {}).map(([, id]) => ({ id, snapshot: null }))
    : Array.isArray(push.update_snapshots)
      ? push.update_snapshots.map(s => ({ id: s.id, snapshot: s.body }))
      : [];

  const total = items.length;
  if (total === 0) return { reversed: 0, failed: 0, failures: [] };

  // create → DELETE each created record; update → PUT the saved snapshot back.
  const batchItems = items.map(({ id, snapshot }) =>
    mode === 'create'
      ? { key: id, endpoint: `${base}/${id}`, method: 'DELETE' }
      : { key: id, endpoint: `${base}/${id}`, method: 'PUT', payload: snapshot }
  );

  let reversed = 0;
  let done = 0;
  const failures = [];

  // Chunks run serially; the proxy fans each chunk out with bounded concurrency.
  // A proxy-level failure (bad creds, 5xx) makes fmxBatchFetch throw — catch it so
  // we still return a complete summary and the undo UI never hangs.
  try {
    await fmxBatchAll({
      projectId: creds?.projectId,
      items: batchItems,
      onChunk: chunk => {
        for (const r of chunk) {
          if (r.ok) reversed++;
          else failures.push({ id: r.key, error: fmxErrorMessage(r?.body, r?.status) });
          done++;
          if (onProgress) onProgress({ done, total, last: { ok: r.ok, label: String(r.key) } });
        }
      },
    });
  } catch (e) {
    const msg = e?.message || 'Undo request failed';
    for (let i = done; i < batchItems.length; i++) {
      failures.push({ id: batchItems[i].key, error: msg });
      if (onProgress) onProgress({ done: i + 1, total, last: { ok: false, label: String(batchItems[i].key) } });
    }
  }

  return { reversed, failed: failures.length, failures };
}
